import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Session } from "next-auth";

import { useToastUpdater } from "../../../context/toastContext/toastContext";
import authService from "../../../services/auth.service";
import getAuthSession from "../browser/getAuthSession";
import { jwtDecodeData } from "../../../helpers/jwtHelper";
import setAuthSession from "../browser/setAuthSession";

type Status = "loading" | "authenticated" | "unauthenticated";

/**
 * Create the Contexts to be used for the Auth Provider.
 */
const AuthStateContext = createContext<unknown>({});
const AuthUpdaterContext = createContext<unknown>({});

const AuthProvider = ({ children, session: defaultSession }: { children: React.ReactNode; session?: Session }) => {
    const [session, setSession] = useState<Session | null>(defaultSession || getAuthSession());
    const [status, setStatus] = useState<Status>("loading");

    useEffect(() => {
        const freshSession = getAuthSession();

        if (freshSession) {
            setSession(freshSession);
        }
    }, []);

    useEffect(() => {
        setStatus(session ? "authenticated" : "unauthenticated");
    }, [session]);

    const state = useMemo(
        () => ({
            session,
            status,
        }),
        [session, status]
    );

    const updaters = useMemo(
        () => ({
            setSession,
            setStatus,
        }),
        [setSession, setStatus]
    );

    return (
        <AuthStateContext.Provider value={state}>
            <AuthUpdaterContext.Provider value={updaters}>{children}</AuthUpdaterContext.Provider>
        </AuthStateContext.Provider>
    );
};

/**
 * Hook for pulling through the toast state.
 * This is separate to the updater as some components will need to
 * read the state but not update it.
 */
const useAuthState = () => {
    const session = useContext(AuthStateContext) as { session?: Session; status: Status };

    if (typeof session === "undefined") {
        throw new Error("useAuthState must be used within a AuthProvider");
    }
    return session;
};

function useAuthUpdater() {
    interface AuthUpdater {
        setSession: (session: Session | null) => void;
        setStatus: (status: Status) => void;
    }

    const updaterState = useContext(AuthUpdaterContext) as AuthUpdater;
    const { addToast } = useToastUpdater();

    if (typeof updaterState === "undefined") {
        throw new Error("useAuthUpdater must be used within a AuthProvider");
    }

    const { setSession, setStatus } = updaterState;

    const refreshSession = async () => {
        try {
            const response = await authService.getFreshUserSession();

            const session = jwtDecodeData(response.session) as Session;
            setAuthSession(
                session
            ); /** We added the auth set session locally too as the cookie wasnt getting set from the server in all cases. */
            setSession(session);
        } catch (error) {
            addToast("Your session could not be refreshed. Please refresh the page", "error", "");
            return;
        }

        setSession(getAuthSession());
    };

    return { setSession, refreshSession, setStatus };
}

export { AuthProvider, useAuthState, useAuthUpdater };
